// @ts-nocheck
import React, { useEffect } from "react";
import { useDispatch, useSelector as useReduxSelector } from "react-redux";
import { useSelector } from "react-redux";
import { fetchServicesFromApi, selectAllServices } from "../../stores/menu/servicesSlice";
import ServiceDetailCard from "../../components/ServiceDetailCard";
import { useLocation, useNavigate } from "react-router-dom";
import { useLanguageContext } from "../../context/languageContext";

const ServiceMenu = () => {
    const dispatch = useDispatch();
    const services = useSelector(selectAllServices);
    const { t, language } = useLanguageContext();
    const navigate = useNavigate();

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const categoryId = queryParams.get("categoryId");

    const businessId = useReduxSelector((state) => state.business.businessId);

    useEffect(() => {
        if (businessId) {
            dispatch(fetchServicesFromApi({ businessid: businessId, roomid: '1' }));
        }
    }, [dispatch, businessId]);

    const selectedCategory = services?.find((cat) => String(cat.id) === String(categoryId));
    const items = selectedCategory?.serviceItems || [];

    const getCategoryName = () => {
        if (!selectedCategory?.categoryTranslation) return selectedCategory?.categoryName || "";
        const map = {
            en: selectedCategory.categoryTranslation.englishName,
            it: selectedCategory.categoryTranslation.italianName,
            sq: selectedCategory.categoryTranslation.albanianName,
            de: selectedCategory.categoryTranslation.germanName,
            es: selectedCategory.categoryTranslation.spanishName,
            pl: selectedCategory.categoryTranslation.polishName,
            ukr: selectedCategory.categoryTranslation.ukrainianName,
        };
        return map[language] || selectedCategory.name;
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 pb-24">
            {/* Decorative Background */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none -z-10">
                <div className="absolute top-0 left-1/2 w-[800px] h-[800px] bg-gradient-to-br from-indigo-100/20 to-purple-100/20 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-gradient-to-tl from-purple-100/20 to-pink-100/20 rounded-full blur-3xl transform translate-x-1/4 translate-y-1/4"></div>
            </div>

            <div className="relative max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8">
                {/* Header Section */}
                <div className="mb-8 sm:mb-10">
                    {/* Back Button */}
                    <button
                        onClick={() => navigate('/room-service')}
                        className="group inline-flex items-center gap-2 px-4 py-2.5 bg-white/80 backdrop-blur-sm text-indigo-600 font-medium rounded-xl sm:rounded-2xl hover:bg-indigo-50 border border-indigo-100/50 shadow-sm hover:shadow-md transition-all duration-300 mb-6"
                    >
                        <svg className="h-4 w-4 transform group-hover:-translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        <span>{t("Back to Categories")}</span>
                    </button>

                    {/* Category Title */}
                    {selectedCategory && (
                        <div className="relative">
                            <div className="flex items-center gap-4">
                                <div className="h-12 sm:h-14 w-1.5 rounded-full bg-gradient-to-b from-indigo-500 via-purple-500 to-pink-500"></div>
                                <div>
                                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 font-clash">
                                        {getCategoryName()}
                                    </h1>
                                    <p className="text-slate-500 text-sm sm:text-base mt-1">
                                        {items.length} {items.length === 1 ? t("service available") : t("services available")}
                                    </p>
                                </div>
                            </div>

                            {/* Decorative Element */}
                            <div className="absolute -top-2 -left-4 w-20 h-20 bg-gradient-to-br from-indigo-100/40 to-purple-100/40 rounded-full blur-2xl -z-10"></div>
                        </div>
                    )}
                </div>

                {/* Services Grid */}
                {items.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 sm:py-24">
                        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl shadow-slate-200/50 p-8 sm:p-12 text-center max-w-md border border-slate-100">
                            <div className="w-20 h-20 bg-gradient-to-br from-slate-100 to-indigo-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-slate-800 mb-3">
                                {categoryId ? t("No services available") : t("Select a category")}
                            </h3>
                            <p className="text-slate-500 text-sm mb-6">
                                {categoryId
                                    ? t("No services available in this category.")
                                    : t("Please select a category to view services.")}
                            </p>
                            <button
                                onClick={() => navigate('/room-service')}
                                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 shadow-lg shadow-indigo-200"
                            >
                                {t("Browse Categories")}
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                </svg>
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-5">
                        {items.map((service, index) => (
                            <div
                                key={service.id}
                                className="animate-fade-in-up"
                                style={{ animationDelay: `${index * 75}ms` }}
                            >
                                <ServiceDetailCard product={service} categoryItems={items} />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* CSS for animation */}
            <style>{`
                @keyframes fade-in-up {
                    0% {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    100% {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fade-in-up {
                    animation: fade-in-up 0.5s ease-out forwards;
                    opacity: 0;
                }
            `}</style>
        </div >
    );
};

export default ServiceMenu;
